import { MobileDrawer } from '@components/MobileDrawer/MobileDrawer';
import CheckIcon from '@mui/icons-material/Check';
import ViewListIcon from '@mui/icons-material/ViewList';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import classNames from 'classnames';
import { useState } from 'react';
import css from './MobileViewModeButton.module.scss';
import { VIEW_MODE_LABELS, ViewMode } from './ViewModeToggle';

const VIEW_MODE_OPTIONS: { mode: ViewMode; label: string; icon: React.ReactNode }[] = [
  { mode: 'list', label: VIEW_MODE_LABELS.list, icon: <ViewListIcon /> },
  { mode: 'grid', label: VIEW_MODE_LABELS.grid, icon: <ViewModuleIcon /> },
];

interface MobileViewModeButtonProps {
  value: ViewMode;
  /** Fired only on an actual change — never with the already-active mode. */
  onChange: (mode: ViewMode) => void;
}

/**
 * Single pill showing the CURRENT view mode; tapping it opens a bottom drawer to
 * pick a view — the same select-from-drawer flow as the mobile search
 * result-type switch.
 *
 * Controlled rather than store-bound: the Library page (media) and the Libraries
 * page (libraries) both use it against different preference keys.
 */
export const MobileViewModeButton = ({ value, onChange }: MobileViewModeButtonProps) => {
  const [drawerOpen, setDrawerOpen] = useState(false);

  const handleSelect = (mode: ViewMode) => {
    if (mode !== value) {
      onChange(mode);
    }
  };

  const current = VIEW_MODE_OPTIONS.find((o) => o.mode === value) ?? VIEW_MODE_OPTIONS[0];

  return (
    <>
      <button
        className={css.button}
        onClick={() => setDrawerOpen(true)}
        aria-haspopup="true"
        aria-expanded={drawerOpen}
        aria-label={`Change view (current: ${current.label})`}
      >
        {current.icon}
      </button>

      <MobileDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onOpen={() => setDrawerOpen(true)}
        frosted
      >
        <div className={css.drawerContent}>
          <span className={css.title}>View</span>
          <div className={css.options}>
            {VIEW_MODE_OPTIONS.map((option) => (
              <button
                key={option.mode}
                className={classNames(css.option, {
                  [css.selected]: value === option.mode,
                })}
                onClick={() => {
                  handleSelect(option.mode);
                  setDrawerOpen(false);
                }}
              >
                <span className={css.optionIcon}>{option.icon}</span>
                <span className={css.optionLabel}>{option.label}</span>
                {value === option.mode && <CheckIcon className={css.checkIcon} />}
              </button>
            ))}
          </div>
        </div>
      </MobileDrawer>
    </>
  );
};
